/**
 * "Today" on the DATA's calendar — not the viewer's browser, not the server.
 *
 * Every metric row in this product carries a plain YYYY-MM-DD stamped by the
 * ad platform in the ad account's timezone, which is Asia/Bangkok across every
 * connected account. Deriving "today" from toISOString() (UTC) made every
 * rolling window and freshness label lag one day between midnight and 07:00
 * Bangkok — a 06:00 sync still read "ข้อมูลถึงเมื่อวาน" (numbers audit F3).
 *
 * Platform API code (sync date windows, LINE's signed-request date) stays on
 * UTC deliberately: those follow each API's contract, not the data calendar.
 */
export const DATA_TIMEZONE = 'Asia/Bangkok';

/** Today's YYYY-MM-DD in the data calendar. en-CA formats as YYYY-MM-DD. */
export const todayDataDate = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: DATA_TIMEZONE }).format(new Date());

/** The YYYY-MM-DD, on the data calendar, of an ISO instant (e.g. a row's timestamptz). */
export const dataDateOf = (iso: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: DATA_TIMEZONE }).format(new Date(iso));

/** Shift a YYYY-MM-DD by whole days — pure date arithmetic, timezone-free. */
export const shiftDataDate = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Whole days from `from` to `to`, timezone-free like `shiftDataDate`.
 *
 * Negative when `to` precedes `from`. Both ends are plain YYYY-MM-DD on the
 * data calendar, so this is calendar arithmetic and never crosses a DST or
 * offset boundary.
 */
export const daysBetween = (from: string, to: string): number =>
  Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  );

/**
 * One bucket of a time series, named by the days it covers.
 *
 * The trend arrays used to travel as four bare numbers with nothing saying
 * which days they were or which end was newest — so "spend fell 40% last week"
 * was a sentence the model could write about a bucket that held six days
 * against three that held seven. A bucket is seven days on the live path and
 * one day in the demo fixtures, which is precisely why the span is carried
 * rather than assumed.
 */
export interface TrendBucket {
  /** First day of the bucket, inclusive, on the data calendar. */
  start: string;
  /** Last day of the bucket, inclusive. */
  end: string;
  /**
   * True for the bucket whose last day is today. Today is still accruing, and
   * the ad platforms restate the last few days as attribution lands, so a dip
   * in this bucket may be the calendar rather than performance.
   */
  includesToday: boolean;
}

/**
 * `count` contiguous buckets of `span` days ending on `endDate`, oldest first.
 *
 * Generated rather than discovered, which is the point: bucketing whatever rows
 * come back leaves a week with no spend simply absent, so position 2 of the
 * array silently becomes a fortnight away from position 1 instead of a week.
 * Every bucket here is the same length whether or not anything ran in it.
 */
export const buildTrendBuckets = (
  count: number,
  span = 7,
  endDate: string = todayDataDate(),
): TrendBucket[] => {
  const today = todayDataDate();
  return Array.from({ length: Math.max(0, count) }, (_, i) => {
    const end = shiftDataDate(endDate, -(count - 1 - i) * span);
    return { start: shiftDataDate(end, -(span - 1)), end, includesToday: end === today };
  });
};

/** Which bucket a date belongs to, or -1 when it falls outside the series. */
export const trendBucketIndex = (buckets: TrendBucket[], isoDate: string): number =>
  buckets.findIndex((b) => isoDate >= b.start && isoDate <= b.end);

/** The 1st of the month of a YYYY-MM-DD date. */
export const monthStartOf = (isoDate: string): string => `${isoDate.slice(0, 7)}-01`;

/** The 1st of the following month. */
export const nextMonthStart = (isoDate: string): string => {
  const d = new Date(`${monthStartOf(isoDate)}T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
};

/** Days in the month of a YYYY-MM-DD date. */
export const daysInMonthOf = (isoDate: string): number => {
  const d = new Date(`${monthStartOf(isoDate)}T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
};
